/**
 * Super-admin login mutation (Batches 27c + 31a.2.1).
 *
 * The backend ``/auth/login`` admin-host route returns ONE OF two shapes
 * per ``SAAS_ARCHITECTURE.md`` MFA section:
 *
 *   - Direct mint (SA has not opted into MFA): ``{access_token, ...}``.
 *     Hook fires the existing ``loginSuccess`` happy path; the page
 *     navigates to ``/admin/tenants``.
 *   - MFA pending (SA has an active mfa_credentials row):
 *     ``{mfa_pending_token, next_step, ...}``. Hook stashes the pending
 *     token in the auth store; the page opens the ``MfaVerifyModal`` (which
 *     renders the enroll or verify surface off ``next_step``). passkey-v2 P0
 *     loop fix — the old ``/admin/login/mfa-{enroll,verify}`` routes are gone.
 *
 * Per HARD-9 the hook clears any prior mfa_pending state on every
 * mutate — both the success path (handled in ``loginSuccess`` /
 * ``mfaPendingStart``) and the error path (``onError``).
 *
 * Per HARD-10 the mfa_pending token lives in-memory only — Zustand
 * store WITHOUT persist middleware. ``useAuthStore`` is already
 * non-persisted; ``mfaPendingStart`` writes to the in-memory slot only.
 *
 * Kept separate from useLogin.ts on purpose: the request shapes differ
 * (no tenant_slug here) and a shared abstraction would be more
 * confusing than two small hooks.
 */
import { useMutation } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import {
  type LoginResponse,
  isMfaPendingResponse,
} from "@/lib/loginResponse";
import { useAuthStore } from "@/stores/useAuthStore";

type AdminLoginInput = { email: string; password: string };

export function useAdminLogin() {
  return useMutation<LoginResponse, ApiError, AdminLoginInput>({
    mutationFn: async ({ email, password }) => {
      const resp = await apiClient.post<LoginResponse>("/auth/login", {
        email,
        password,
      });
      return resp.data;
    },
    onMutate: () => {
      // HARD-9: drop any prior mfa_pending state before issuing a new
      // login attempt, so a stale pending token never bleeds into a
      // fresh attempt's routing decision.
      useAuthStore.getState().mfaPendingClear();
    },
    onSuccess: (data) => {
      const store = useAuthStore.getState();
      if (isMfaPendingResponse(data)) {
        // MFA-required branch. Stash pending token + TTL timer; let
        // the calling component navigate based on next_step.
        store.mfaPendingStart(
          data.mfa_pending_token,
          data.expires_in,
          data.next_step,
          // WA-1b: pass available_methods so the verify screen can route
          // to the right challenge flow. Absent/empty → graceful TOTP fallback.
          { availableMethods: data.available_methods },
        );
      } else {
        // Direct-mint branch — legacy happy path. ``loginSuccess`` also
        // calls ``mfaPendingClear`` to belt-and-suspenders a stale slot.
        store.loginSuccess(data.access_token);
      }
    },
    onError: () => {
      // HARD-9: clear pending state on every failed login.
      useAuthStore.getState().mfaPendingClear();
    },
  });
}
